import {
  CircleCheckIcon,
  FlameIcon,
  TargetIcon,
  TrophyLineIcon,
} from 'assets/icons';
import Button from 'components/button';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import SlideOverHeader from 'components/SlideOver/component/header';
import useSlideOver from 'components/SlideOver/useSlideOver';
import StyledInput from 'components/styledInput/StyledInput';
import { addDays, fromIsoDay, isoDay } from 'helper/timeLabels';
import { useNotifyChange } from 'hooks/useChangeSignal';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useText from 'hooks/useText';
import { longDay } from 'pages/planning/planningItems';
import { useState } from 'react';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import { Dots, ReviewLine } from './FeelingCard';
import { FEELINGS } from './statsFormat';
import { isoWeek } from './statsPeriods';

// The slide-over of a weekly review; the statistics reload its list when one
// is saved.
export const REVIEW_PANEL = 'stats_review';
export const STATS_REVIEW_SAVED = 'statsReviewSaved';

const savedOn = (review) => longDay(new Date(review.createdAt)).toLowerCase();

// The figures of the week, as grey chips.
const Chips = ({ totals }) => {
  const { text, plural } = useText('stats');
  return (
    <div className="flex flex-wrap gap-2">
      {[
        plural('chipCalls', totals.calls),
        text('chipRdv', { count: totals.rdvTaken }),
        plural('chipSales', totals.sales),
      ].map((chip) => (
        <span
          key={chip}
          className="rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium leading-4 text-gray-900"
        >
          {chip}
        </span>
      ))}
    </div>
  );
};

// A scale of the form: five buttons, one ticked.
const Scale = ({ name, label, hint, value, onChange }) => (
  <div className="flex flex-col gap-2">
    <p className="flex items-baseline gap-1.5 text-sm font-medium leading-5 text-gray-900">
      {label}
      {hint && (
        <span className="text-xs font-normal leading-4 text-gray-400">
          {hint}
        </span>
      )}
    </p>
    <div className="flex gap-2" role="radiogroup" aria-label={label}>
      {[1, 2, 3, 4, 5].map((score) => (
        <button
          key={score}
          type="button"
          role="radio"
          name={name}
          aria-checked={value === score}
          onClick={() => onChange(score)}
          className={`flex h-10 flex-1 items-center justify-center rounded-[10px] border text-sm font-semibold leading-5 transition-colors ${value === score ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-900 hover:bg-gray-50'}`}
        >
          {score}
        </button>
      ))}
    </div>
  </div>
);

const FieldTitle = ({ icon: Icon, children }) => (
  <p className="flex items-center gap-2 text-sm font-medium leading-5 text-gray-900">
    <Icon wh={16} color="currentColor" />
    {children}
  </p>
);

/**
 * « Mon bilan · S43 », as in the prototype: four scales from 1 to 5, a
 * victory and one challenge, then « Bilan enregistré » and the streak of
 * reviews. The agency manager reads a commercial's review (`mode: 'read'`).
 * `objID`: { week, mode, personName, totals, review, reviewedWeeks }.
 */
const ReviewContent = ({
  week,
  mode,
  personName,
  totals,
  review,
  reviewedWeeks = [],
}) => {
  const { text } = useText('stats');
  const { toggleSlideOver } = useSlideOver();
  const { call } = useCrmContext();
  const notifySaved = useNotifyChange(STATS_REVIEW_SAVED);
  const [form, setForm] = useState(() => ({
    ...Object.fromEntries(FEELINGS.map((key) => [key, review?.[key] ?? null])),
    victory: review?.victory ?? '',
    challenge: review?.challenge ?? '',
  }));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const monday = fromIsoDay(week);
  const n = isoWeek(monday);
  const nextMonday = addDays(monday, 7);
  nextMonday.setHours(0, 0, 0, 0);
  const reminds = nextMonday > new Date();
  const close = () => toggleSlideOver();
  const set = (key, value) =>
    setForm((previous) => ({ ...previous, [key]: value }));
  const reading = mode === 'read';
  const title = reading
    ? text('reviewOfTitle', { name: personName, n })
    : text('reviewTitle', { n });

  const save = () => {
    setSaving(true);
    call(crmApi.saveStatsReview, {
      weekStart: week,
      ...Object.fromEntries(FEELINGS.map((key) => [key, form[key]])),
      victory: form.victory.trim(),
      challenge: form.challenge.trim(),
    }).then(
      (result) => {
        setSaving(false);
        setSaved(result);
        notifySaved();
      },
      (error) => {
        setSaving(false);
        toast.error(error?.message || text('saveError'));
      },
    );
  };

  const header = (description) => (
    <SlideOverHeader hide={close} closeLabel={text('close')}>
      <span className="flex flex-col">
        {title}
        <span className="text-sm font-normal text-gray-600">{description}</span>
      </span>
    </SlideOverHeader>
  );
  const footer = (buttons) => (
    <div className="flex-shrink-0 border-t border-gray-100">
      <SlideOverFooter>{buttons}</SlideOverFooter>
    </div>
  );
  const closeButton = (
    <Button color="SECONDARY" size="SM" onClick={close}>
      {text('close')}
    </Button>
  );

  if (saved) {
    const has = (day) =>
      isoDay(day) === week || reviewedWeeks.includes(isoDay(day));
    let streak = 0;
    for (let day = monday; has(day); day = addDays(day, -7)) streak += 1;
    const challenge = saved.challenge
      .replace(/[.!]+$/, '')
      .replace(/^[A-ZÀ-Ý](?=[a-zà-ÿ])/, (first) => first.toLowerCase());
    return (
      <>
        {header(text('savedOn', { day: savedOn(saved) }))}
        <SlideOverBody>
          <div className="flex flex-col items-center gap-5 pt-10 text-center">
            <span className="flex size-[72px] items-center justify-center rounded-full bg-green-50 text-gray-900">
              <CircleCheckIcon wh={32} color="currentColor" />
            </span>
            <p className="text-xl font-bold leading-[26px] tracking-[-0.4px] text-gray-900">
              {text('savedTitle')}
            </p>
            <p className="text-sm leading-5 text-gray-600">
              {challenge
                ? `${text('savedChallenge', { challenge })}${reminds ? text('savedReminder') : ''}`
                : text('savedNoChallenge')}
            </p>
            <div className="flex flex-col items-center gap-2 rounded-[14px] bg-gray-100 px-5 py-4">
              <p className="flex items-center gap-1.5 text-sm font-semibold leading-5 text-gray-900">
                <FlameIcon wh={16} color="currentColor" />
                {streak > 1
                  ? text('savedStreak', { count: streak })
                  : text('savedFirst')}
              </p>
              <div className="flex gap-2">
                {[-3, -2, -1, 0, 1].map((offset) => {
                  const day = addDays(monday, 7 * offset);
                  const done = offset <= 0 && has(day);
                  return (
                    <span
                      key={offset}
                      className="flex flex-col items-center gap-1"
                    >
                      <span
                        className={`size-3.5 rounded-full ${done ? 'bg-gray-900' : 'bg-gray-200'}`}
                      />
                      <span
                        className={`text-xs leading-4 ${offset > 0 ? 'text-gray-400' : 'text-gray-600'}`}
                      >
                        {text('weekShort', { n: isoWeek(day) })}
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </SlideOverBody>
        {footer(closeButton)}
      </>
    );
  }

  if (reading)
    return (
      <>
        {header(
          review
            ? text('savedOnBy', { day: savedOn(review) })
            : text('noReviewYet'),
        )}
        <SlideOverBody>
          <div className="flex flex-col gap-5">
            <Chips totals={totals} />
            {review &&
              FEELINGS.map((key) => (
                <div key={key} className="flex items-center gap-2">
                  <p className="flex-1 text-sm leading-5 text-gray-600">
                    {text(`feeling_${key}`)}
                  </p>
                  <Dots value={review[key]} />
                </div>
              ))}
            {review?.victory && (
              <ReviewLine icon={TrophyLineIcon}>{review.victory}</ReviewLine>
            )}
            {review?.challenge && (
              <ReviewLine icon={TargetIcon}>{review.challenge}</ReviewLine>
            )}
          </div>
        </SlideOverBody>
        {footer(closeButton)}
      </>
    );

  const ready = FEELINGS.every((key) => form[key]);
  return (
    <>
      {header(
        review
          ? text('savedOn', { day: savedOn(review) })
          : text('reviewIntro'),
      )}
      <SlideOverBody>
        <div className="flex flex-col gap-6">
          <Chips totals={totals} />
          <p className="flex justify-between text-xs leading-4 text-gray-400">
            <span>{text('scaleLow')}</span>
            <span>{text('scaleHigh')}</span>
          </p>
          {FEELINGS.map((key) => (
            <Scale
              key={key}
              name={key}
              label={text(`scale_${key}`)}
              hint={
                key === 'questioning' ? text('scale_questioningHint') : null
              }
              value={form[key]}
              onChange={(score) => set(key, score)}
            />
          ))}
          <div className="flex flex-col gap-2">
            <FieldTitle icon={TrophyLineIcon}>
              {text('victoryLabel')}
            </FieldTitle>
            <StyledInput
              type="textarea"
              placeholder={text('victoryPlaceholder')}
              value={form.victory}
              onChange={(event) => set('victory', event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <FieldTitle icon={TargetIcon}>{text('challengeLabel')}</FieldTitle>
            <StyledInput
              type="text"
              placeholder={text('challengePlaceholder')}
              value={form.challenge}
              onChange={(event) => set('challenge', event.target.value)}
            />
          </div>
        </div>
      </SlideOverBody>
      {footer(
        <>
          <Button color="SECONDARY" size="SM" onClick={close}>
            {review ? text('close') : text('later')}
          </Button>
          <Button
            color="PRIMARY"
            size="SM"
            disabled={!ready || saving}
            onClick={save}
          >
            {text('saveReview')}
          </Button>
        </>,
      )}
    </>
  );
};

const ReviewPanel = () => {
  const { objID } = UseContext();
  if (!objID?.week) return null;
  return <ReviewContent key={`${objID.week}-${objID.mode}`} {...objID} />;
};

export default ReviewPanel;
